import React from 'react';
import { NavLink } from 'react-router-dom';

import ButtonBase from '@mui/material/ButtonBase';
import { makeStyles } from 'tss-react/mui';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import navAppIcon from 'assets/logos/nav-app-icon.svg';
import QbpWordmark from 'assets/logos/qbp-wordmark.inline.svg';
import SimplifiWordmark from 'assets/logos/simplifi-wordmark.inline.svg';
import simplifiLogoTextLite from 'assets/logos/simplifi-lite-text-dark.svg';
import simplifiLogoTextWhiteLite from 'assets/logos/simplifi-lite-text-white.svg';
import { useAppSelector } from 'hooks/redux';
import useThemeMode from 'hooks/useThemeMode';

import { navLabelFadeIn, navLabelFadeOut } from '../commonNavStyles';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(0, 1, 0.5),
    width: '100%',
    boxSizing: 'border-box',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: `${theme.spacing(0.5)} 2px`,
    borderRadius: theme.shape.radius.md,
    overflow: 'hidden',
    transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.short }),
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  icon: {
    flex: 'none',
    display: 'block',
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  wordmark: {
    flex: 'none',
    display: 'block',
    color: theme.palette.text.primary,
    opacity: 1,
    transition: navLabelFadeIn(theme),
    '& .nav-wordmark-quicken': {
      color: theme.palette.text.secondary,
    },
  },
  wordmarkHidden: {
    opacity: 0,
    transition: navLabelFadeOut(theme),
  },
}));

type NavBrandProps = {
  expanded: boolean;
  disabled: boolean;
  onClick: () => void;
};

export const NavBrand = React.memo(function NavBrand({
  expanded,
  disabled,
  onClick,
}: NavBrandProps): React.JSX.Element {
  const { classes, cx } = useStyles();
  const topTier = useAppSelector(entitlementsSelectors.getTopTierEntitlement);
  const themeMode = useThemeMode();
  const wordmarkClassName = cx(classes.wordmark, !expanded && classes.wordmarkHidden);

  const isBusiness = topTier?.id === 'ACME_HAB';
  const Wordmark = isBusiness ? QbpWordmark : SimplifiWordmark;

  return (
    <div className={classes.root}>
      <ButtonBase
        component={NavLink}
        to='/'
        id='logo-nav'
        aria-label='Dashboard'
        disabled={disabled}
        onClick={onClick}
        className={classes.button}
      >
        <img src={navAppIcon} alt='' className={classes.icon} />
        {topTier?.id === 'ACME_LITE' ? (
          <img
            src={themeMode === 'light' ? simplifiLogoTextLite : simplifiLogoTextWhiteLite}
            alt='Quicken'
            aria-hidden={!expanded}
            className={wordmarkClassName}
          />
        ) : (
          <Wordmark
            role='img'
            aria-label={isBusiness ? 'Quicken Business & Personal' : 'Quicken Simplifi'}
            aria-hidden={!expanded}
            className={wordmarkClassName}
          />
        )}
      </ButtonBase>
    </div>
  );
});
